import {Directive, inject, Injector, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {LazyPhraseService} from '@im-ng/text';
import {TextActPhrase} from '@im-ng/text/act';
import {isEmpty} from '@js-dot/core';
import {DotTranslateService} from '@ng-dot/text';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {LayoutService} from '../services/layout.service';
import {PageMain, PageUpperLinkType} from '../typings/page-list';

@Directive()

/**
 * PageMagicComponent 와 연동되는 컴포넌트 기반
 *
 * @see PageMagicComponent
 */

export abstract class MainComponentBase<T = any> implements PageMain, OnDestroy {
	@ViewChild('_toolLeft_', {static: true})
	_toolLeft_!: TemplateRef<any>;

	@ViewChild('_toolRight_', {static: true})
	_toolRight_!: TemplateRef<any>;

	@ViewChild('_toolDetail_', {static: true})
	_toolDetail_!: TemplateRef<any>;

	@ViewChild('_tools_', {static: true})
	_tools_!: TemplateRef<any>;

	protected _destroy$ = new Subject<void>();
	protected _takeUntilDestroy: any = takeUntil(this._destroy$);

	protected _hasHeading!: boolean;
	protected _heading$ = new BehaviorSubject<string>(null);
	protected _description$ = new BehaviorSubject<string | Promise<string> | Observable<string>>(null);

	protected _injector: Injector;
	protected _translate: DotTranslateService;

	protected _layout: LayoutService;
	protected _act = inject<LazyPhraseService<TextActPhrase>>(LazyPhraseService).load('global-as');

	heading$ = this._heading$.pipe(distinctUntilChanged());

	descript$ = this._description$.pipe(
		filter(value => !isEmpty(value)),
		distinctUntilChanged()
	) as Observable<string>;

	/**
	 * Display Anchor for Move To Upper on Title and Toolbar
	 */
	useUpperLink$ = new BehaviorSubject<PageUpperLinkType>(false);
	useFullscreenLink$ = new BehaviorSubject<boolean>(false);


	constructor(public feature?: string) {
		const _injector = inject(Injector)
		this._injector = _injector;
		this._translate = _injector.get(DotTranslateService);
		this._layout = _injector.get(LayoutService);

		if (feature) {
			this._setDefinedHeading(feature);
		}
	}

	ngOnDestroy() {
		this._destroy$.next();
	}

	/**
	 * 쪽 제목 설정
	 * @description
	 * `im-page-magic`을 연동
	 * @param heading
	 */
	protected _setHeading(heading: string) {
		const {_heading$} = this;
		this._hasHeading = true;
		_heading$.next(heading);
		return this;
	}

	/**
	 * 쪽, 브라우저 제목 설정
	 * @description
	 * `im-page-magic` 사용시 연동
	 */
	protected _setTitle(heading: string) {
		this._setHeading(heading);
	}

	/**
	 * 정의 단어로 부터 제목 설정
	 */
	protected _setDefinedHeading(heading: string, as?: string) {
		const definedHeading = this._translate.define(heading);

		return this._setHeading(
			as ?
				this._act.get('$' + as, definedHeading) :
				definedHeading
		);
	}

	/**
	 * 설명 설정
	 * @param description 설명
	 */
	protected _setDescription(description: string | Promise<string> | Observable<string>) {
		const {_description$} = this;
		_description$.next(description);
	}

	/**
	 * 정의된 단어로 부터 설명 사용
	 * @param defineId 단어 식별값
	 * @protected
	 */
	protected _setDefinedDescription(defineId: string) {
		return this._setDescription(this._translate.word(defineId));
	}

	//////////////////////////////
	// 지연 후 제거

	/**
	 * @deprecated `_setDescription` 사용
	 */
	protected _setDescript(descript: string) {
		console.warn('`_setDescript`는 재명명 되었습니다, `_setDescription`을 사용 하세요.');
		return this._setDescription(descript);
	}
}
